import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import Flecha_down from "../../../assets/img/Flecha_down.svg";
import Flecha_up from "../../../assets/img/Flecha_up.svg";
import LineChart from "../../Charts/LineCharts";
import ThreeAxisLineChart from "../../Charts/ThreeAxisLineChart";
import { getAlertIconVentas } from "../../utils/get";
import { getTanquesSifoneadosFluctuacionesCierreMensual } from "../../utils/tanquesSifoneados";
import { getLocaleStringRoundedInt, parseDate, parseDateString } from "../../utils/utils";
import { hideGreyBackground, showGreyBackground } from "../../utils/windows";
import PrintFileHeader from "./PrintFileHeader";
import check_green from "../../../assets/img/Check_green.svg";
import check_yellow from "../../../assets/img/check_yellow.svg";
import check_red from "../../../assets/img/atencion_rojo.svg";
import closeIcon from "../../../assets/img/cross-icon.svg";
import chevronRight from "../../../assets/img/chevron-right.svg";

const responsiveModalStyles = {
  content: {
    width: "343px",
    maxWidth: "calc(100vw - 32px)",
    padding: 0,
    boxSizing: "border-box",
    position: "relative",
    left: "auto",
    top: "auto",
    right: "auto",
    bottom: "auto",
    transform: "none",
    margin: "24px 0 32px",
    borderRadius: 8,
    overflow: "visible"
  },
  overlay: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    background: "rgba(0, 0, 0, 0.5)",
    zIndex: 50,
    overflowY: "auto",
    overflowX: "hidden"
  }
};

const orderKeyByFilter = {
  FECHA: "fecha",
  ENTRADAS: "entradas",
  CALIBRACIONES: "calibraciones",
  MOVIMIENTOS_BODEGA: "movimientos_bodega",
  SALIDAS: "salidas",
  SALIDA_ACUMULADA: "salida_acumulada_lts",
  INVENTARIO_TEORICO: "inventario_teorico",
  MEDICION_FISICA: "medicion_fisica_proyectada",
  FLUCTUACION_DIARIA: "fluctuacion_diaria_lts",
  FLUCTUACION_ACUMULADA: "fluctuacion_acumulada_lts",
  FLUCTUACION_ACUMULADA_PORCENTAJE: "fluctuacion_porcentaje",
  VALIDADO: "validado"
};

const validadoSortValue = {
  VERDE: 1,
  AMARILLA: 2,
  ROJO: 3
};

const printHeaderColumns = [
  "Fecha",
  "Entradas (lts)",
  "Calibraciones",
  "Mov. de Bodega",
  "Salidas (lts)",
  "Salida Acum. (lts)",
  "Inventario Teorico (lts)",
  "Med. Fisica Proyect. (lts)",
  "Fluctuacion Diaria (lts)",
  "Fluctuacion Acum (lts)",
  "% Fluctuacion Acum"
];

const PRINT_FIRST_PAGE_ROW_LIMIT = 24;
const PRINT_NEXT_PAGE_ROW_LIMIT = 31;

const getPrintChunks = (rows) => {
  if (!rows.length) return [];

  const chunks = [rows.slice(0, PRINT_FIRST_PAGE_ROW_LIMIT)];
  let startIndex = PRINT_FIRST_PAGE_ROW_LIMIT;

  while (startIndex < rows.length) {
    chunks.push(rows.slice(startIndex, startIndex + PRINT_NEXT_PAGE_ROW_LIMIT));
    startIndex += PRINT_NEXT_PAGE_ROW_LIMIT;
  }

  return chunks;
};

const getDateSortValue = (value) => {
  const text = `${value || ""}`;
  const digits = text.replace(/\D/g, "");

  if (digits.length === 8) {
    if (digits.startsWith("20") || digits.startsWith("19")) return Number(digits);

    const day = digits.substring(0, 2);
    const month = digits.substring(2, 4);
    const year = digits.substring(4, 8);
    return Number(`${year}${month}${day}`);
  }

  const timestamp = Date.parse(text);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getSortValue = (cierre, filterkey) => {
  const dataKey = orderKeyByFilter[filterkey];
  const value = cierre?.[dataKey];

  if (filterkey === "FECHA") return getDateSortValue(value);
  if (filterkey === "VALIDADO") return validadoSortValue[value] || 0;
  if (typeof value === "number") return value;

  const parsedNumber = parseFloat(`${value}`.replace(/\./g, "").replace(",", "."));
  if (!Number.isNaN(parsedNumber)) return parsedNumber;

  return `${value || ""}`.toLowerCase();
};

const HistorialFluctuacionesTable = ({
  tanqueData,
  tanquesData,
  tableRef,
  total,
  displayTable,
  cabecera,
  selectedEds,
  selectedPeriodo,
  rutConcesionario
}) => {
  const [selectedResponsiveCierre, setSelectedResponsiveCierre] = useState()
  const [sortConfig, setSortConfig] = useState()
  const cierres = tanqueData?.cierres || []
  const hasCierres = tanquesData && tanquesData.length > 0 && cierres.length > 0
  const responsiveEmptyMessage = tanquesData
    ? "No se han encontrado tanques."
    : "Seleccione una Estación de Servicio y un Periodo para ver la información."

  useEffect(() => {
    if (!selectedResponsiveCierre) return;
    showGreyBackground();
    return () => hideGreyBackground();
  }, [selectedResponsiveCierre]);

  const orderTable = (filterkey, order) => {
    setSortConfig({ filterkey, order });
  };

  const sortedCierres = useMemo(() => {
    if (!sortConfig) return cierres;

    const orderDirection = sortConfig.order === "asc" ? 1 : -1;

    return [...cierres].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.filterkey);
      const bValue = getSortValue(b, sortConfig.filterkey);

      if (aValue === bValue) return 0;
      if (aValue === undefined || aValue === null || aValue === "") return 1;
      if (bValue === undefined || bValue === null || bValue === "") return -1;

      if (typeof aValue === "string" || typeof bValue === "string") {
        return `${aValue}`.localeCompare(`${bValue}`) * orderDirection;
      }

      return (aValue > bValue ? 1 : -1) * orderDirection;
    });
  }, [cierres, sortConfig]);

  const data = {
    tanques: [
      {
        tanque: "Tanque 1",
        material: "GAS 93",
        medicion_inicial:123,
        cierres: [
          { fecha: "1/12/2022", entradas: 1234, salidas: 3456, medicion: 6543 },
          { fecha: "2/12/2022", entradas: 1234, salidas: 3456, medicion: 6543 },
        ],
      },
      {
        tanque: "Tanque 2",
        material: "GAS 97",
        medicion_inicial:456,
        cierres: [
          { fecha: "1/12/2022", entradas: 1234, salidas: 3456, medicion: 6543 },
          { fecha: "2/12/2022", entradas: 1234, salidas: 3456, medicion: 6543 },
        ],
      }
    ],
  };

  function getAlertaTooltipText(color){
    if(color === "ROJO")
      return "Fuera de Rango"

    else if(color === "AMARILLA")
      return "Dentro del Rango"
  
    else if(color === "VERDE")
      return "Sin Fluctuación"
  }

  function getAlertaImg(color){
    if(color === "ROJO")
      return <img src={check_red} alt="" />
  
    else if(color === "AMARILLA")
      return <img src={check_yellow} alt="" />
  
    else if(color === "VERDE")
      return <img src={check_green} alt="" />
  }

  const getResponsiveDetailRows = (cierre) => [
    { label: "Entradas (lts)", value: getLocaleStringRoundedInt(cierre.entradas), alignRight: true },
    { label: "Calibraciones", value: getLocaleStringRoundedInt(cierre.calibraciones), alignRight: true },
    { label: "Mov. de Bodega", value: getLocaleStringRoundedInt(cierre.movimientos_bodega), alignRight: true },
    { label: "Salidas (lts)", value: getLocaleStringRoundedInt(cierre.salidas), alignRight: true },
    { label: "Salida Acum. (lts)", value: getLocaleStringRoundedInt(cierre.salida_acumulada_lts), alignRight: true },
    { label: "Inventario Teórico (lts)", value: getLocaleStringRoundedInt(cierre.inventario_teorico), alignRight: true },
    { label: "Med. Física Proyect. (lts)", value: getLocaleStringRoundedInt(cierre.medicion_fisica_proyectada), alignRight: true },
    { label: "Fluctuación Diaria (lts)", value: getLocaleStringRoundedInt(cierre.fluctuacion_diaria_lts), alignRight: true },
    { label: "Fluctuación Acum (lts)", value: getLocaleStringRoundedInt(cierre.fluctuacion_acumulada_lts), alignRight: true },
    { label: "% Fluctuación Acum", value: cierre.fluctuacion_porcentaje.toString().replace(".", ","), alignRight: true },
    { label: "Validado", value: getAlertaImg(cierre.validado), alignRight: true }
  ];

  const closeResponsiveModal = () => setSelectedResponsiveCierre()
  const printChunks = getPrintChunks(sortedCierres);

  return (
    <div className="historial-fluctuaciones-table-container" ref={tableRef}>
      <PrintFileHeader 
        cabecera={cabecera} 
        selectedEds={selectedEds} 
        selectedPeriodo={selectedPeriodo} 
        tanqueData={tanqueData} 
        rutConcesionario={rutConcesionario} 
      />
      {displayTable && 
        <>
        <div className="historial-fluctuaciones-print-table-wrapper">
          <div className="historial-fluctuaciones-print-table">
            {hasCierres &&
              printChunks.map((printChunk) => {
                const firstRow = printChunk[0];
                const lastRow = printChunk[printChunk.length - 1];
                const printPageKey = `historial-fluct-print-page-${firstRow?.fecha}-${lastRow?.fecha}`;
                const isLastChunk = printChunk === printChunks[printChunks.length - 1];

                return (
                  <div
                    key={printPageKey}
                    className="historial-fluctuaciones-print-page"
                  >
                    <div className="historial-fluctuaciones-print-row historial-fluctuaciones-print-row--header">
                      {printHeaderColumns.map((column) => (
                        <div key={`${printPageKey}-${column}`}>{column}</div>
                      ))}
                    </div>
                    {printChunk.map((cierre) => (
                      <div
                        key={`historial-fluct-print-${cierre.fecha}-${cierre.salida_acumulada_lts}-${cierre.fluctuacion_acumulada_lts}`}
                        className="historial-fluctuaciones-print-row"
                      >
                        <div>{parseDateString(cierre.fecha)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.entradas)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.calibraciones)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.movimientos_bodega)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.salidas)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.salida_acumulada_lts)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.inventario_teorico)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.medicion_fisica_proyectada)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.fluctuacion_diaria_lts)}</div>
                        <div>{getLocaleStringRoundedInt(cierre.fluctuacion_acumulada_lts)}</div>
                        <div>{cierre.fluctuacion_porcentaje.toString().replace(".", ",")}</div>
                      </div>
                    ))}
                    {isLastChunk &&
                      <div className="historial-fluctuaciones-print-row historial-fluctuaciones-print-total-row">
                        <div>Total</div>
                        <div>{getLocaleStringRoundedInt(total?.TOTAL_ENTRADAS)}</div>
                        <div>{getLocaleStringRoundedInt(total?.TOTAL_CALIBRACIONES)}</div>
                        <div>{getLocaleStringRoundedInt(total?.TOTAL_MOV_BODEGA)}</div>
                        <div>{getLocaleStringRoundedInt(total?.TOTAL_SALIDAS)}</div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                    }
                  </div>
                );
              })
            }
            {!hasCierres &&
              <div className="historial-fluctuaciones-print-empty">
                {responsiveEmptyMessage}
              </div>
            }
          </div>
        </div>
        <div className="historial-fluctuaciones-table-media-fix-container show-only-desktop">
          <table className="historial-fluctuaciones-table historial-fluctuaciones-table-media-fix">
            <tr>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Fecha
                    <div className="arrows">
                      <img
                        onClick={() => orderTable("FECHA", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() => orderTable("FECHA", "desc")}
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Entradas (lts)
                    <div className="arrows">
                      <img
                        onClick={() => orderTable("ENTRADAS", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() => orderTable("ENTRADAS", "desc")}
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Calibraciones
                    <div className="arrows">
                      <img
                        onClick={() => orderTable("CALIBRACIONES", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() => orderTable("CALIBRACIONES", "desc")}
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Mov. de<br />Bodega
                    <div className="arrows mtop">
                      <img
                        onClick={() => orderTable("MOVIMIENTOS_BODEGA", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() => orderTable("MOVIMIENTOS_BODEGA", "desc")}
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Salidas (lts)
                    <div className="arrows">
                      <img
                        onClick={() => orderTable("SALIDAS", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() => orderTable("SALIDAS", "desc")}
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Salida Acum.<br />(lts)
                    <div className="arrows">
                      <img
                        onClick={() => orderTable("SALIDA_ACUMULADA", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() =>
                          orderTable("SALIDA_ACUMULADA", "desc")
                        }
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Inventario Teórico (lts)
                    <div className="arrows mtop">
                      <img
                        onClick={() =>
                          orderTable("INVENTARIO_TEORICO", "asc")
                        }
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() =>
                          orderTable("INVENTARIO_TEORICO", "desc")
                        }
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Med. Física Proyect. (lts)
                    <div className="arrows mtop">
                      <img
                        onClick={() => orderTable("MEDICION_FISICA", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() => orderTable("MEDICION_FISICA", "desc")}
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Fluctuación Diaria (lts)
                    <div className="arrows mtop">
                      <img
                        onClick={() =>
                          orderTable("FLUCTUACION_DIARIA", "asc")
                        }
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() =>
                          orderTable("FLUCTUACION_DIARIA", "desc")
                        }
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    Fluctuación<br />Acum (lts)
                    <div className="arrows mtop">
                      <img
                        onClick={() =>
                          orderTable("FLUCTUACION_ACUMULADA", "asc")
                        }
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() =>
                          orderTable("FLUCTUACION_ACUMULADA", "desc")
                        }
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data">
                  <div className="th-text">
                    %&nbsp;Fluctuación Acum
                    <div className="arrows mtop">
                      <img
                        onClick={() =>
                          orderTable("FLUCTUACION_ACUMULADA_PORCENTAJE", "asc")
                        }
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() =>
                          orderTable("FLUCTUACION_ACUMULADA_PORCENTAJE", "desc")
                        }
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
              <th>
                <div className="th-data validado">
                  <div className="th-text">
                    Validado
                    <div className="arrows">
                      <img
                        onClick={() => orderTable("VALIDADO", "asc")}
                        src={Flecha_up}
                      ></img>
                      <img
                        onClick={() => orderTable("VALIDADO", "desc")}
                        src={Flecha_down}
                      ></img>
                    </div>
                  </div>
                </div>
              </th>
            </tr>
            <tr className="table-separator-row-border-top">
              <td />
            </tr>

          
              {tanquesData?<>

              {hasCierres?<>
                <tbody>
                {sortedCierres.map((cierre,key)=>(
                  <tr key={key} className="data">
                    <td className="align-right">{parseDateString(cierre.fecha)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.entradas)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.calibraciones)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.movimientos_bodega)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.salidas)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.salida_acumulada_lts)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.inventario_teorico)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.medicion_fisica_proyectada)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.fluctuacion_diaria_lts)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.fluctuacion_acumulada_lts)}</td>
                    <td className="align-right">{cierre.fluctuacion_porcentaje.toString().replace(".", ',')}</td>
                    <td className={`alert-container-cierre-diario align-right cell-border-top-right`}>
                      {getAlertaImg(cierre.validado)}
                      <div className="alert-tooltip confirmacion">
                        <div className="content">
                          {getAlertaTooltipText(cierre.validado)}
                        </div>
                      </div>
                    </td>
                    {/* <td className="align-right">

                      {getAlertIconVentas(cierre.validado)}
                      <div className="alert-tooltip confirmacion">
                        <div className="content">
                          {getAlertaTooltipText(cierre.validado)}
                        </div>
                      </div>
                    </td> */}
                  </tr>
                ))}
                </tbody>
                <tr className="table-separator-row-border-top">
                  <td />
                </tr>
                <tbody>
                  <tr className="data">
                    <td className="align-right">Total</td>
                    <td className="align-right">{getLocaleStringRoundedInt(total?.TOTAL_ENTRADAS)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(total?.TOTAL_CALIBRACIONES)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(total?.TOTAL_MOV_BODEGA)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(total?.TOTAL_SALIDAS)}</td>
                    <td className="align-left" colSpan="100%"></td>
                  </tr>
                </tbody>  
              </>:<>
                <tbody>
                  <tr className="data">
                    <td className="align-left" colSpan="100%">
                      No se han encontrado tanques.
                    </td>
                  </tr>
                </tbody>
              </>}
                
              </>:<>
                <tbody>
                  <tr className="data">
                    <td className="align-left" colSpan="100%">
                      Seleccione una Estación de Servicio y un Periodo para ver la información.
                    </td>
                  </tr>
                </tbody>
              </>}
          </table>
        </div>
        <div className="historial-fluctuaciones-responsive-table-shell hide-desktop">
          <table className="historial-fluctuaciones-responsive-table responsive-table separated-th-table">
            <colgroup>
              <col className="historial-fluctuaciones-responsive-col historial-fluctuaciones-responsive-col--fecha" />
              <col className="historial-fluctuaciones-responsive-col historial-fluctuaciones-responsive-col--entradas" />
              <col className="historial-fluctuaciones-responsive-col historial-fluctuaciones-responsive-col--salidas" />
              <col className="historial-fluctuaciones-responsive-col historial-fluctuaciones-responsive-col--fluctuacion-diaria show-only-tablet" />
              <col className="historial-fluctuaciones-responsive-col historial-fluctuaciones-responsive-col--fluctuacion-acum show-only-tablet" />
              <col className="historial-fluctuaciones-responsive-col historial-fluctuaciones-responsive-col--porcentaje-acum show-only-tablet" />
              <col className="historial-fluctuaciones-responsive-col historial-fluctuaciones-responsive-col--validado" />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div className="th-data">
                    Fecha
                    <div className="arrows">
                      <img onClick={() => orderTable("FECHA", "asc")} src={Flecha_up} alt="" />
                      <img onClick={() => orderTable("FECHA", "desc")} src={Flecha_down} alt="" />
                    </div>
                  </div>
                </th>
                <th>
                  <div className="th-data">
                    Entradas<br className="show-only-mobile" /> (lts)
                    <div className="arrows">
                      <img onClick={() => orderTable("ENTRADAS", "asc")} src={Flecha_up} alt="" />
                      <img onClick={() => orderTable("ENTRADAS", "desc")} src={Flecha_down} alt="" />
                    </div>
                  </div>
                </th>
                <th>
                  <div className="th-data">
                    Salidas<br className="show-only-mobile" /> (lts)
                    <div className="arrows">
                      <img onClick={() => orderTable("SALIDAS", "asc")} src={Flecha_up} alt="" />
                      <img onClick={() => orderTable("SALIDAS", "desc")} src={Flecha_down} alt="" />
                    </div>
                  </div>
                </th>
                <th className="show-only-tablet">
                  <div className="th-data">
                    Fluctuación<br />Diaria (lts)
                    <div className="arrows">
                      <img onClick={() => orderTable("FLUCTUACION_DIARIA", "asc")} src={Flecha_up} alt="" />
                      <img onClick={() => orderTable("FLUCTUACION_DIARIA", "desc")} src={Flecha_down} alt="" />
                    </div>
                  </div>
                </th>
                <th className="show-only-tablet">
                  <div className="th-data">
                    Fluctuación<br />Acum (lts)
                    <div className="arrows">
                      <img onClick={() => orderTable("FLUCTUACION_ACUMULADA", "asc")} src={Flecha_up} alt="" />
                      <img onClick={() => orderTable("FLUCTUACION_ACUMULADA", "desc")} src={Flecha_down} alt="" />
                    </div>
                  </div>
                </th>
                <th className="show-only-tablet">
                  <div className="th-data">
                    % Fluctuación<br />Acum
                    <div className="arrows">
                      <img onClick={() => orderTable("FLUCTUACION_ACUMULADA_PORCENTAJE", "asc")} src={Flecha_up} alt="" />
                      <img onClick={() => orderTable("FLUCTUACION_ACUMULADA_PORCENTAJE", "desc")} src={Flecha_down} alt="" />
                    </div>
                  </div>
                </th>
                <th>
                  <div className="th-data validado">Validado</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {hasCierres && sortedCierres.map((cierre, key) => (
                <React.Fragment key={`historial-fluct-responsive-${cierre.fecha || key}`}>
                  <tr>
                    <td>{parseDateString(cierre.fecha)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.entradas)}</td>
                    <td className="align-right">{getLocaleStringRoundedInt(cierre.salidas)}</td>
                    <td className="align-right show-only-tablet">{getLocaleStringRoundedInt(cierre.fluctuacion_diaria_lts)}</td>
                    <td className="align-right show-only-tablet">{getLocaleStringRoundedInt(cierre.fluctuacion_acumulada_lts)}</td>
                    <td className="align-right show-only-tablet">{cierre.fluctuacion_porcentaje.toString().replace(".", ",")}</td>
                    <td className="align-center historial-fluctuaciones-validado-cell">
                      {getAlertaImg(cierre.validado)}
                      <button className="historial-fluctuaciones-responsive-detail-button" onClick={() => setSelectedResponsiveCierre(cierre)}>
                        <img src={chevronRight} alt="" />
                      </button>
                    </td>
                  </tr>
                  {key < sortedCierres.length - 1 &&
                    <tr className="separator-table-row historial-fluctuaciones-row-separator">
                      <td colSpan="7" />
                    </tr>
                  }
                </React.Fragment>
              ))}
              {!hasCierres &&
                <tr>
                  <td className="align-left" colSpan="7">
                    {responsiveEmptyMessage}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <Modal
          isOpen={Boolean(selectedResponsiveCierre)}
          onRequestClose={closeResponsiveModal}
          style={responsiveModalStyles}
          className="responsive-modal historial-fluctuaciones-detail-modal"
        >
          {selectedResponsiveCierre &&
            <>
              <div className="historial-fluctuaciones-detail-modal__header">
                <div>
                  <h1>Fluctuaciones</h1>
                  <p>{parseDateString(selectedResponsiveCierre.fecha)}</p>
                </div>
                <button onClick={closeResponsiveModal}>
                  <img src={closeIcon} alt="Cerrar modal" />
                </button>
              </div>
              <table className="responsive-table separated-th-table historial-fluctuaciones-detail-table">
                <thead>
                  <tr>
                    <th><div className="th-data">Detalle</div></th>
                    <th><div className="th-data">Valor</div></th>
                  </tr>
                </thead>
                <tbody>
                  {getResponsiveDetailRows(selectedResponsiveCierre).map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td className={row.alignRight ? "align-right" : undefined}>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="main-button mobile-button historial-fluctuaciones-detail-modal__close" onClick={closeResponsiveModal}>
                Cerrar
              </button>
            </>
          }
        </Modal>
        </>
      }      
    </div>
  );
};

export default HistorialFluctuacionesTable;
